import moment from 'moment';
import {
  AllocationForTeam,
  AllocationForUser,
  ResourceAllocations,
  ResourceType,
} from 'api/resource-management/types';
import { INVALID_ROLE_ID } from 'components/organisms/resource-management/utils/common';
import {
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { overAllocationsAsUIFormat } from 'components/organisms/resource-management/utils/overallocation';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import {
  AllocationEvent,
  SchedulerEventType,
  TeamAllocationEvent,
  UserAllocationEvent,
} from '../../event-types';
import { ExcludeNonWorkingDaysParam } from '../../types';
import { getSchedulerEventProps, getSchedulerEventsClass } from './common';

const allocationForUserAsEvent = (
  {
    project: { projectId },
    id,
    startDate,
    endDate,
    minutes,
    projectPhase,
    billable,
    role,
    referenceTeam,
    medium,
    overAllocations,
  }: AllocationForUser,
  userId: number,
  exclusionParam: ExcludeNonWorkingDaysParam,
  softAllocationsEnabled?: boolean,
): UserAllocationEvent => {
  const startDateMoment = moment(startDate).startOf('day');
  const endDateMoment = moment(endDate).endOf('day');

  /**
   * roleId and referenceTeamId will be set only for replaced users
   */

  return {
    type: SchedulerEventType.Allocation,
    id,
    resourceId: resourceIdForProjectAsLeaf({
      projectId,
      userId,
      roleId: role?.roleId,
      referenceTeamId: referenceTeam?.teamId,
    }),
    userId,
    roleId: role?.roleId,
    referenceTeamId: referenceTeam?.teamId,
    startDate: startDateMoment.toDate(),
    endDate: endDateMoment.toDate(),
    minutes,
    totalMinutes:
      numberOfWorkingDaysBetweenDates(
        startDateMoment,
        endDateMoment,
        exclusionParam.workingDaysConfig,
      ) * minutes,
    projectPhase,
    billable,
    projectId,
    overAllocations,
    overAllocationsForUI: overAllocationsAsUIFormat(overAllocations),
    medium,
    ...getSchedulerEventProps(SchedulerEventType.Allocation, {
      cls: getSchedulerEventsClass(
        SchedulerEventType.Allocation,
        `${softAllocationsEnabled ? medium : ''}`.toLowerCase(),
      ),
    }),
  };
};

const allocationForTeamAsEvent = (
  {
    project: { projectId },
    id,
    startDate,
    endDate,
    minutes,
    projectPhase,
    billable,
    medium,
  }: AllocationForTeam,
  teamId: number,
  roleId: number,
  exclusionParam: ExcludeNonWorkingDaysParam,
  softAllocationsEnabled?: boolean,
): TeamAllocationEvent => {
  const startDateMoment = moment(startDate).startOf('day');
  const endDateMoment = moment(endDate).endOf('day');

  return {
    type: SchedulerEventType.Allocation,
    id,
    resourceId: resourceIdForTeamAsLeaf(projectId, teamId),
    teamId,
    roleId,
    startDate: startDateMoment.toDate(),
    endDate: endDateMoment.toDate(),
    minutes,
    totalMinutes:
      numberOfWorkingDaysBetweenDates(
        startDateMoment,
        endDateMoment,
        exclusionParam.workingDaysConfig,
      ) * minutes,
    projectPhase,
    billable,
    projectId,
    overAllocations: [],
    overAllocationsForUI: [],
    medium,
    ...getSchedulerEventProps(SchedulerEventType.Allocation, {
      cls: getSchedulerEventsClass(
        SchedulerEventType.Allocation,
        `${softAllocationsEnabled ? medium : ''}`.toLowerCase(),
      ),
    }),
  };
};

export const convertAllocationsByUsersOrTeamsToEvents = (
  groupedAllocations: ResourceAllocations,
  exclusionParam: ExcludeNonWorkingDaysParam,
  softAllocationsEnabled?: boolean,
): AllocationEvent[] =>
  groupedAllocations.reduce((result: AllocationEvent[], resource) => {
    return result.concat(
      resource.allocations.map((allocation) => {
        if (resource.type === ResourceType.User) {
          return allocationForUserAsEvent(
            allocation,
            resource.user.userId,
            exclusionParam,
            softAllocationsEnabled,
          );
        }

        return allocationForTeamAsEvent(
          allocation,
          resource.team.teamId,
          resource.team.roleId ?? INVALID_ROLE_ID,
          exclusionParam,
          softAllocationsEnabled,
        );
      }),
    );
  }, []);
